import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

import { useGlobalStore } from "./stores/global";
import AgeCheck from "./pages/ageCheck.vue";
import TermsAcceptance from "./pages/termsAcceptance.vue";
import PeopleCheck from "./pages/peopleCheck.vue";
import { onMounted, onBeforeUnmount, watch, ref } from "vue";
import AgeCheckFailed from "./pages/ageCheckFailed.vue";
import GameMove from "./pages/gameMove.vue";
import PhotoCapture from "./pages/photoCapture.vue";
import ShareInstructions from "./pages/shareInstructions.vue";
import RaffleMove from "./pages/raffleMove.vue";
import TermsAndConditions from "./pages/termsAndConditions.vue";
import i18n from "@/plugins/i18n";
import ShotAnimation from "./components/ShotAnimation.vue";
import backgroundImageGreen from "@/assets/images/backgrounds/bg-green-xl.jpg";
import ThatsEnough from "./pages/thatsEnough.vue";
import { useGameTracking } from "@/composables/useGameTracking";


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();
const sessionInitialized = ref(false);

const preventTouchMove = (e: TouchEvent) => {
  if (e.cancelable) {
    e.preventDefault();
  }
};
const preventTouchMoveOptions = { passive: false };

const removeScroll = () => {
  document.addEventListener(
    "touchmove",
    preventTouchMove,
    preventTouchMoveOptions
  );
  console.log("scroll removed");
};

const addScroll = () => {
  document.removeEventListener(
    "touchmove",
    preventTouchMove,
    preventTouchMoveOptions
  );
  console.log("scroll added");
};

watch(
  () => globalStore.currentStep,
  async (newStep, oldStep) => {
    if (newStep === "termsAndConditions") {
      addScroll();
    } else {
      removeScroll();
    }
    
    // Only update session if it's already initialized and step actually changed
    // Skip gameMove and thatsEnough as they are handled by game_started and game_finished calls
    // This tracks: ageCheck, shotAnimation, peopleCheck, ageCheckFailed, termsAndConditions
    if (
      sessionInitialized.value &&
      newStep &&
      newStep !== oldStep &&
      newStep !== "gameMove" &&
      newStep !== "photoCapture" &&
      newStep !== "shareInstructions" &&
      newStep !== "thatsEnough"
    ) {
      console.log(`[App] Step changed to '${newStep}', updating session`);
      try {
        await session.updateSessionProgress(kpiTracker.getKpiData(), { step: newStep });
        console.log(`[App] Session updated with step '${newStep}'`);
      } catch (error) {
        console.error(`[App] Error updating session with step '${newStep}':`, error);
      }
    }
  }
);

// Handle page unload - update session before user leaves
// Use sendBeacon/fetch with keepalive to ensure the request is sent even when closing
const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  // Use the special unload function that uses sendBeacon or fetch with keepalive
  // This ensures the request is sent even when the page is closing
  session.updateSessionProgressOnUnload(kpiTracker.getKpiData());
};

onMounted(async () => {
  let lang = new URL(location.href).searchParams.get("lang");
  if (lang) {
    i18n.global.locale = lang;
  }
  
  // Change step FIRST to show correct UI immediately (before session initialization)
  // The watch won't trigger updateSessionProgress because sessionInitialized is still false
  globalStore.changeStep("ageCheck");
  
  // Handle scroll removal for initial step
  if (globalStore.currentStep === "termsAndConditions") {
    addScroll();
  } else {
    removeScroll();
  }
  
  // Initialize session after changing step
  // IMPORTANT: Don't reset progress to 0 - it will be loaded from existing session if available
  const campaignId = new URL(location.href).searchParams.get("campaignId") ?? "";
  console.log("[App] Initializing session on page load");
  try {
    await session.getFingerprintAndInitializeSession(campaignId);
    // DON'T update session progress here - it's already handled in createUpdateSession
    // Updating here would overwrite the progress that was just loaded from the existing session
    const currentProgress = kpiTracker.progress?.value ?? 0;
    console.log("[App] Session initialized, current progress:", currentProgress);
    sessionInitialized.value = true;
  } catch (error) {
    console.error("[App] Error initializing session on page load:", error);
    sessionInitialized.value = true; // Set to true even on error to allow future updates
  }
  
  // Add beforeunload listener
  window.addEventListener("beforeunload", handleBeforeUnload);
});

onBeforeUnmount(() => {
  // Remove beforeunload listener
  window.removeEventListener("beforeunload", handleBeforeUnload);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "font-regular",
    style: _normalizeStyle({ backgroundImage: `url(${_unref(backgroundImageGreen)})` })
  }, [
    (_unref(globalStore).currentStep === 'ageCheck')
      ? (_openBlock(), _createBlock(AgeCheck, { key: 0 }))
      : (_unref(globalStore).currentStep === 'shotAnimation')
        ? (_openBlock(), _createBlock(ShotAnimation, { key: 1 }))
        : (_unref(globalStore).currentStep === 'termsAcceptance')
          ? (_openBlock(), _createBlock(TermsAcceptance, { key: 2 }))
          : (_unref(globalStore).currentStep === 'peopleCheck')
            ? (_openBlock(), _createBlock(PeopleCheck, { key: 3 }))
            : (_unref(globalStore).currentStep === 'ageCheckFailed')
              ? (_openBlock(), _createBlock(AgeCheckFailed, { key: 4 }))
              : (_unref(globalStore).currentStep === 'gameMove')
                ? (_openBlock(), _createBlock(GameMove, { key: 5 }))
                : (_unref(globalStore).currentStep === 'photoCapture')
                  ? (_openBlock(), _createBlock(PhotoCapture, { key: 6 }))
                  : (_unref(globalStore).currentStep === 'shareInstructions')
                    ? (_openBlock(), _createBlock(ShareInstructions, { key: 7 }))
                    : (_unref(globalStore).currentStep === 'raffleMove')
                      ? (_openBlock(), _createBlock(RaffleMove, { key: 8 }))
                      : (_unref(globalStore).currentStep === 'termsAndConditions')
                        ? (_openBlock(), _createBlock(TermsAndConditions, { key: 9 }))
                        : (_unref(globalStore).currentStep === 'thatsEnough')
                          ? (_openBlock(), _createBlock(ThatsEnough, { key: 10 }))
                          : _createCommentVNode("", true)
  ], 4))
}
}

})